import { isAccountsOnly, isLogisticsOnly, isProjectsOnly, OBJECTS, type SearchHit } from '@crm/shared';
import { type BottomItem, BottomNav, InstallBanner, InstallMenuItem } from './MobileApp';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type CSSProperties, type ReactNode, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { api, lastServerContact, qs } from '../lib/api';
import { SuggestButton } from './Suggest';
import { startAutoIcons } from '../lib/autoIcons';
import { jumpTo, startMotion } from '../lib/motion';
import { startSegSlide } from '../lib/segSlide';
import { leavePage, staggerPage } from '../lib/pageStagger';
import { WelcomeIntro } from './WelcomeIntro';
import { useSession } from '../lib/session';
import { usePref } from '../lib/prefs';
import { currentSeason, SEASON_LABEL } from '../lib/season';
import { NotificationsBell } from './Notifications';
import { Popups } from './Popups';
import { QuickSettings } from './QuickSettings';
import { SHOWN_OBJECTS } from '../lib/shownObjects';
import { isDark, savedTheme, setTheme } from '../lib/theme';
import { Icon } from './Icon';

const TABS = [
  { to: '/', label: 'Home', end: true },
  { to: '/o/account', label: 'Companies' },
  { to: '/o/contact', label: 'Contacts' },
  { to: '/o/opportunity', label: 'Opportunities' },
  { to: '/intelligence', label: 'Intelligence' },
  { to: '/calendar', label: 'Calendar' },
  { to: '/forecasts', label: 'Forecasts' },
  { to: '/dashboards', label: 'Dashboards' },
  { to: '/reports', label: 'Reports' },
];

interface Tab { to: string; label: string; end?: boolean }
interface Area { key: string; label: string; icon: string; hint: string; home: string; tabs: Tab[] }
const AREA_KEY = 'crm.area';

/** The area a page belongs to. Home (the sales home page) is in Sales; pages outside every area keep the current one. */
function areaOf(areas: Area[], path: string, current: string, search = ''): string {
  if (path === '/') return 'sales';
  // KPIs: the pre-sales KPIs belong to Pre-sales; the sales KPIs stay in the area they were opened from (Sales or
  // Management), else Management — never Pre-sales just because it is listed first.
  if (path === '/kpi' || path.startsWith('/kpi/')) {
    if (new URLSearchParams(search).get('view') === 'presales' && areas.some((a) => a.key === 'presales')) return 'presales';
    if (current === 'sales' || current === 'management') return current;
    return areas.some((a) => a.key === 'management') ? 'management' : current;
  }
  const owns = (a: Area) => a.tabs.some((t) => { const to = t.to.split('?')[0]!; return to !== '/' && (path === to || path.startsWith(`${to}/`)); });
  if (areas.find((a) => a.key === current && owns(a))) return current;
  // A page shared by two areas (Logistics, Projects) goes to the area it is the main page of.
  const main = areas.find((a) => a.home !== '/' && (path === a.home || path.startsWith(`${a.home}/`)));
  return (main ?? areas.find(owns))?.key ?? current;
}

/** Each area's tile: its own colour (a soft gradient) with a white line symbol. */
const AREA_ICONS: Record<string, { from: string; to: string; d: string }> = {
  // A briefcase with a rising line: selling.
  sales: { from: '#3b82f6', to: '#1d4ed8', d: 'M4 8h16v11H4zM9 8V6a1.5 1.5 0 0 1 1.5-1.5h3A1.5 1.5 0 0 1 15 6v2M7.5 15l3-3 2 2 4-4' },
  // A drafting compass over a page: preparing the offer.
  presales: { from: '#a855f7', to: '#6d28d9', d: 'M7 4.5h7l3.5 3.5v11.5H7zM14 4.5V8h3.5M10 17l2-6 2 6M10.7 15h2.6' },
  // A dashboard gauge with bars: managing.
  management: { from: '#14b8a6', to: '#0f766e', d: 'M4.5 19.5h15M7 16v-4M11 16V8.5M15 16v-6M19 16V6' },
  // A building under a crane: execution.
  projects: { from: '#fb923c', to: '#c2410c', d: 'M4 20h16M6 20V11h5v9M13 20V8h5v12M8 4.5h11M17 4.5v3M8 4.5v4' },
  // A delivery truck.
  logistics: { from: '#fbbf24', to: '#b45309', d: 'M3.5 7h10v9h-10zM13.5 10h3.5l3 3v3h-6.5M7 18.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3ZM17 18.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z' },
  // A shopping cart: buying.
  purchasing: { from: '#f472b6', to: '#be185d', d: 'M3.5 5h2l2 10h10l2-7H7M10 19.5a1.2 1.2 0 1 0 0-2.4 1.2 1.2 0 0 0 0 2.4ZM17 19.5a1.2 1.2 0 1 0 0-2.4 1.2 1.2 0 0 0 0 2.4Z' },
  // A banknote with a coin: money.
  accounts: { from: '#22c55e', to: '#15803d', d: 'M3.5 7.5h17v9h-17zM12 14.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5ZM6.5 10v4M17.5 10v4' },
  // Sliders: settings.
  setup: { from: '#64748b', to: '#334155', d: 'M5 6h14M5 12h14M5 18h14M9 4.5v3M15 10.5v3M8 16.5v3' },
};

export function AreaIcon({ name, size = 'm' }: { name: string; size?: 's' | 'm' }) {
  const a = AREA_ICONS[name] ?? AREA_ICONS.setup!;
  return (
    <span className={`area-ico ${size}`} style={{ background: `linear-gradient(135deg, ${a.from}, ${a.to})` }} aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d={a.d} /></svg>
    </span>
  );
}

interface Group { key: string; label: string; items: Tab[] }
const MINI_KEY = 'crm.side-mini';
const HIDDEN_KEY = 'crm.side-hidden';

/** A menu link is on the page shown: its path (exactly for Home) and, when it has one, its ?view=… too. */
function linkScore(t: Tab, path: string, search: string): number {
  const [to, q] = t.to.split('?') as [string, string | undefined];
  const onPath = t.end ? path === to : path === to || path.startsWith(`${to}/`);
  if (!onPath) return 0;
  if (!q) return 1;
  const want = new URLSearchParams(q);
  const have = new URLSearchParams(search);
  return [...want].every(([k, v]) => have.get(k) === v) ? 2 : 0;
}

/**
 * The menu on the left: each area (Sales, Pre-sales, Projects…) with its pages. The page shown is marked by a soft
 * highlight that slides to the page clicked. Collapsed, only the icons show (the names as hover hints). On phones it
 * opens from ☰ over the page.
 */
function Sidebar({ groups, active, mini, onMini, onPick, onSignOut, brand }: {
  groups: Group[]; active: string | null; mini: boolean; onMini: () => void; onPick: (g: Group) => void; onSignOut: () => void; brand: ReactNode;
}) {
  const nav = useRef<HTMLElement>(null);
  const glow = useRef<HTMLSpanElement>(null);
  const navigate = useNavigate();
  const [dark, setDark] = useState(() => isDark(savedTheme()));
  // The highlight moves to the active link (measured after layout; no slide on the first show).
  useLayoutEffect(() => {
    const n = nav.current;
    const g = glow.current;
    if (!n || !g) return;
    const place = () => {
      const el = n.querySelector<HTMLElement>('a[aria-current="page"]');
      if (!el) { g.style.opacity = '0'; return; }
      g.style.opacity = '1';
      g.style.transform = `translateY(${el.offsetTop}px)`;
      g.style.height = `${el.offsetHeight}px`;
      // The page shown stays in view in a long menu.
      if (el.offsetTop < n.scrollTop || el.offsetTop + el.offsetHeight > n.scrollTop + n.clientHeight) n.scrollTop = el.offsetTop - n.clientHeight / 3;
    };
    place();
    const t = window.setTimeout(() => g.classList.add('ready'), 50);
    const ro = new ResizeObserver(place);
    ro.observe(n);
    return () => { window.clearTimeout(t); ro.disconnect(); };
  }, [active, mini, groups]);
  return (
    <aside className="side no-print" aria-label="Menu">
      <div className="side-brand">{brand}</div>
      <nav className="side-nav" ref={nav} aria-label="Main">
        <span className="side-glow" ref={glow} aria-hidden="true" />
        {groups.map((g) => (
          <div key={g.key} className="side-group">
            {groups.length > 1 && <p className="side-head"><AreaIcon name={g.key} size="s" /><span>{g.label}</span></p>}
            {g.items.map((t) => {
              const on = active === `${g.key}|${t.to}`;
              return (
                <Link key={t.to} to={t.to} aria-current={on ? 'page' : undefined} className={on ? 'active' : ''} title={mini ? t.label : undefined}
                  onClick={(e) => {
                    onPick(g);
                    // A plain click: the page shown leaves first (a new tab or window opens as usual).
                    if (on || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                    e.preventDefault();
                    leavePage(() => navigate(t.to));
                  }}>
                  <span className="side-ini" aria-hidden="true" data-l={t.label.slice(0, 1)} />
                  <span className="side-label">{t.label}</span>
                </Link>
              );
            })}
          </div>
        ))}
      </nav>
      <div className="side-foot">
        <button type="button" className="side-btn" aria-label={dark ? 'Light mode' : 'Dark mode'} title={dark ? 'Light mode' : 'Dark mode'}
          onClick={() => { const next = dark ? 'light' : 'dark'; setTheme(next); setDark(!dark); }}>
          {dark
            ? <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="4" /><path d="M12 2.5v2M12 19.5v2M4.6 4.6l1.4 1.4M18 18l1.4 1.4M2.5 12h2M19.5 12h2M4.6 19.4 6 18M18 6l1.4-1.4" /></svg>
            : <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 14.5A8 8 0 0 1 9.5 4a8 8 0 1 0 10.5 10.5Z" /></svg>}
        </button>
        <button type="button" className="side-btn side-collapse" aria-label={mini ? 'Show the menu names' : 'Collapse the menu'} title={mini ? 'Show the menu names' : 'Collapse the menu'} onClick={onMini}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3.5" y="4.5" width="17" height="15" rx="2.5" /><path d="M9 4.5v15" />{mini ? <path d="m13 10 2 2-2 2" /> : <path d="m15 10-2 2 2 2" />}</svg>
        </button>
        <button type="button" className="side-btn side-out" aria-label="Sign out" title="Sign out" onClick={onSignOut}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3v8M7.5 6.2a7 7 0 1 0 9 0" /></svg>
        </button>
      </div>
    </aside>
  );
}

/**
 * Connection lost: a thin bar slides in (the phone is offline, or the server doesn't answer); it turns green
 * "Back online" when requests get through again. Changes are not queued: they can be saved once back online.
 */
function ConnectionBar() {
  const qc = useQueryClient();
  const [state, setState] = useState<'ok' | 'down' | 'back'>(() => (typeof navigator !== 'undefined' && navigator.onLine === false ? 'down' : 'ok'));
  useEffect(() => {
    let t = 0;
    let wasDown = typeof navigator !== 'undefined' && navigator.onLine === false;
    const set = (ok: boolean) => {
      if (!ok) { window.clearTimeout(t); wasDown = true; setState('down'); return; }
      if (!wasDown) return;
      // Back: say so for 3 s and refresh what is on screen (once, however many signals arrive).
      wasDown = false;
      setState('back');
      t = window.setTimeout(() => setState('ok'), 3000);
      void qc.invalidateQueries();
    };
    const onApi = (e: Event) => set((e as CustomEvent<boolean>).detail);
    const off = () => set(false);
    const on = () => set(true);
    window.addEventListener('crm-connection', onApi);
    window.addEventListener('offline', off);
    window.addEventListener('online', on);
    return () => { window.clearTimeout(t); window.removeEventListener('crm-connection', onApi); window.removeEventListener('offline', off); window.removeEventListener('online', on); };
  }, [qc]);
  if (state === 'ok') return null;
  return (
    <div className={`net-bar ${state}`} role="status">
      {state === 'down' ? 'No connection: changes can’t be saved until you’re back online.' : '✓ Back online'}
    </div>
  );
}

/**
 * The sign-in is about to end: in the last 2 minutes a small ring counts down, with "Stay signed in" (when it
 * ends for inactivity) so nobody loses a half-filled form. A fixed end can't be extended: then it says to save.
 */
function SessionTimer() {
  const { sessionLimits } = useSession();
  const qc = useQueryClient();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => { const t = window.setInterval(() => setNow(Date.now()), 5000); return () => window.clearInterval(t); }, []);
  if (!sessionLimits) return null;
  const hard = Date.parse(sessionLimits.expiresAt);
  // The server records activity every few minutes, so count idle time 5 minutes early to be safe.
  const idle = sessionLimits.idleMinutes ? lastServerContact - 5 * 60_000 + sessionLimits.idleMinutes * 60_000 : Infinity;
  const end = Math.min(hard, idle);
  const left = end - now;
  if (!Number.isFinite(end) || left > 120_000 || left <= 0) return null;
  const secs = Math.ceil(left / 1000);
  const byIdle = idle < hard;
  return (
    <div className="session-warn" role="alertdialog" aria-label="Your sign-in is about to end">
      <span className="session-ring" style={{ '--p': `${(left / 120_000) * 100}%` } as CSSProperties}>{Math.floor(secs / 60)}:{String(secs % 60).padStart(2, '0')}</span>
      <span>{byIdle ? 'You’ll be signed out soon for inactivity.' : 'Your sign-in ends soon: save what you are doing.'}</span>
      {byIdle && <button type="button" className="btn small primary" onClick={() => { void api('/api/auth/session').then(() => setNow(Date.now())); void qc.invalidateQueries({ queryKey: ['notifications'] }); }}>Stay signed in</button>}
    </div>
  );
}

/** Phones: pulling the page down at the top refreshes the data, with a turning arrow. */
function PullToRefresh() {
  const qc = useQueryClient();
  const [pull, setPull] = useState(0);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let y0: number | null = null;
    let d = 0;
    const start = (e: TouchEvent) => {
      const t = e.target as Element | null;
      y0 = window.scrollY <= 0 && !t?.closest('.modal-backdrop, dialog, .board-wrap, .table-wrap, textarea, select') ? (e.touches[0]?.clientY ?? null) : null;
      d = 0;
    };
    const move = (e: TouchEvent) => { if (y0 == null) return; d = Math.max(0, Math.min(110, ((e.touches[0]?.clientY ?? y0) - y0) * 0.5)); setPull(d); };
    const end = () => {
      if (y0 != null && d > 60) {
        setBusy(true);
        void qc.invalidateQueries().finally(() => window.setTimeout(() => setBusy(false), 400));
      }
      y0 = null; d = 0; setPull(0);
    };
    window.addEventListener('touchstart', start, { passive: true });
    window.addEventListener('touchmove', move, { passive: true });
    window.addEventListener('touchend', end);
    return () => { window.removeEventListener('touchstart', start); window.removeEventListener('touchmove', move); window.removeEventListener('touchend', end); };
  }, [qc]);
  if (!pull && !busy) return null;
  return (
    <div className={`ptr${busy ? ' busy' : ''}${pull > 60 ? ' ready' : ''}`} style={{ transform: `translate(-50%, ${busy ? 60 : pull}px)` }} role="status" aria-label={busy ? 'Refreshing' : 'Pull to refresh'}>
      <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" style={{ transform: busy ? undefined : `rotate(${pull * 3}deg)` }}><path d="M20 12a8 8 0 1 1-2.3-5.7M20 4v5h-5" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg>
    </div>
  );
}

/** The searched word marked in a result, with a soft yellow sweep. */
function Highlight({ text, term }: { text: string; term: string }) {
  const i = term ? text.toLowerCase().indexOf(term.toLowerCase()) : -1;
  if (i < 0) return <>{text}</>;
  return <>{text.slice(0, i)}<mark className="hl-sweep">{text.slice(i, i + term.length)}</mark>{text.slice(i + term.length)}</>;
}

/** "Back to top": appears after scrolling down a long page and glides back up. */
function BackToTop() {
  const [on, setOn] = useState(false);
  useEffect(() => {
    const check = () => setOn(window.scrollY > 700);
    window.addEventListener('scroll', check, { passive: true });
    return () => window.removeEventListener('scroll', check);
  }, []);
  if (!on) return null;
  return (
    <button type="button" className="back-to-top no-print" aria-label="Back to top"
      onClick={() => window.scrollTo({ top: 0, behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' })}>
      <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="M12 19V5M5 12l7-7 7 7" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg>
    </button>
  );
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/** Global search: queries each object the user can read and groups the matches. */
function GlobalSearch() {
  const { permissions } = useSession();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const term = useDebounced(q.trim(), 250);
  const navigate = useNavigate();
  const listId = useId();
  const box = useRef<HTMLDivElement>(null);

  // Ranked search across every object the user can read; results are grouped by object.
  const results = useQuery({
    queryKey: ['global-search', term],
    enabled: term.length >= 2,
    queryFn: async () => {
      const { hits } = await api<{ hits: SearchHit[] }>(`/api/search${qs({ q: term, limit: 20 })}`);
      return SHOWN_OBJECTS.filter((o) => permissions.objects[o].read)
        .map((o) => ({ o, hits: hits.filter((h) => h.objectName === o) }))
        .filter((g) => g.hits.length > 0);
    },
  });

  useEffect(() => {
    const close = (e: MouseEvent) => { if (!box.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const go = (path: string) => { setOpen(false); setQ(''); navigate(path); };

  return (
    <div className="gsearch" ref={box}>
      <input
        type="search"
        placeholder="Search companies, contacts and opportunities…"
        aria-label="Search"
        aria-controls={listId}
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}
      />
      {open && term.length >= 2 && (
        <div className="gsearch-pop" id={listId} role="listbox">
          {results.isPending && <p className="muted">Searching…</p>}
          {results.data?.length === 0 && <p className="empty-line">No matches for “{term}”.</p>}
          {results.data?.map(({ o, hits }) => (
            <section key={o}>
              <h4>{OBJECTS[o].pluralLabel}</h4>
              {hits.map((h) => (
                <button key={h.recordId} role="option" aria-selected="false" onClick={() => go(`/o/${o}/${h.recordId}`)}>
                  <Highlight text={h.title || '(no name)'} term={term} />{h.subtitle && <span className="muted small"> · <Highlight text={h.subtitle} term={term} /></span>}
                </button>
              ))}
            </section>
          ))}
        </div>
      )}
    </div>
  );
}


/**
 * Like Ctrl + F5: reloads the whole app from the server, the newest version included, skipping what the browser
 * (and the installed app) kept in its cache. The page you are on opens again.
 */
function RefreshButton() {
  const [spin, setSpin] = useState(false);
  const refresh = async () => {
    setSpin(true);
    try {
      // The installed app's stored copies: dropped, and its service worker asked to fetch the newest version.
      if ('caches' in window) await Promise.all((await caches.keys()).map((k) => caches.delete(k)));
      if ('serviceWorker' in navigator) await Promise.all((await navigator.serviceWorker.getRegistrations()).map((r) => r.update().catch(() => undefined)));
    } catch { /* a full reload still happens */ }
    // A fresh address so the browser can't answer from its cache, then back to the same page.
    const url = new URL(window.location.href);
    url.searchParams.set('_r', String(Date.now()));
    window.location.replace(url.toString());
  };
  // The "_r" mark of a refresh is taken off the address once the page has loaded.
  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.has('_r')) { url.searchParams.delete('_r'); window.history.replaceState(window.history.state, '', url.toString()); }
  }, []);
  return (
    <button className={`icon-btn refresh-btn${spin ? ' spinning' : ''}`} aria-label="Refresh" title="Refresh: reload the CRM with the newest version and data (like Ctrl + F5)" onClick={() => void refresh()}>
      <svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true"><path d="M16 10a6 6 0 1 1-1.8-4.3M16 3.5v3.2h-3.2" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>
    </button>
  );
}

/** "Log in as": always on top while an administrator uses the CRM as someone else, with the way back. */
function LoginAsBanner() {
  const { user, impersonator } = useSession();
  const [busy, setBusy] = useState(false);
  if (!impersonator) return null;
  const back = async () => {
    setBusy(true);
    try {
      const r = await api<{ signedOut: boolean }>('/api/auth/return-to-admin', { method: 'POST', body: {} });
      window.location.assign(r.signedOut ? '/login' : '/setup/users');
    } catch { window.location.assign('/login'); }
  };
  return (
    <div className="login-as-bar" role="status">
      <span><Icon name="eye" />You are logged in as <b>{user.name}</b> (until {new Date(impersonator.until).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}). What you change is recorded as done by you, {impersonator.name}.</span>
      <button className="btn small" disabled={busy} onClick={() => void back()}>{busy ? 'Returning…' : 'Return to my account'}</button>
    </div>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const { user, organization, permissions, customObjects, ceo, purchasing } = useSession();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [quick, setQuick] = useState(false);
  // Season touches (National Day, Founding Day, Ramadan evenings): on unless the person turned them off.
  const season = currentSeason();
  const [seasonOn, setSeasonOn] = usePref<boolean>('season-touches', true);
  // Line icons on titles, tiles, tabs and buttons across every page.
  useEffect(() => startAutoIcons(), []);
  // Motion: figures count up, loading lines shimmer, status changes flash, the bell rings.
  useEffect(() => startMotion(), []);
  // Filter tabs: the white pill slides to the tab clicked.
  useEffect(() => startSegSlide(), []);
  // The Help and account menus close when a link in them is used, on a click outside, and with Escape.
  const location = useLocation();
  // Pre-sales engineers start in My Desk; everyone else in Sales. Then the last area used.
  const firstArea = permissions.system.presalesWork && !permissions.system.manageSetup ? 'presales' : 'sales';
  const [areaKey, setAreaKey] = useState(() => { try { return localStorage.getItem(AREA_KEY) ?? firstArea; } catch { return firstArea; } });
  const keepArea = (k: string) => { setAreaKey(k); try { localStorage.setItem(AREA_KEY, k); } catch { /* not remembered */ } };
  useEffect(() => { document.querySelectorAll('details.usermenu[open], details.app-switcher[open]').forEach((d) => d.removeAttribute('open')); }, [location.pathname, location.search]);
  useEffect(() => {
    const close = (e: Event) => {
      if (e instanceof KeyboardEvent && e.key !== 'Escape') return;
      document.querySelectorAll<HTMLDetailsElement>('details.usermenu[open], details.app-switcher[open]').forEach((d) => {
        const t = e.target as Node | null;
        if (e instanceof KeyboardEvent || !t || !d.contains(t) || (t instanceof Element && t.closest('.menu a, .menu button'))) d.removeAttribute('open');
      });
    };
    document.addEventListener('click', close);
    document.addEventListener('keydown', close);
    return () => { document.removeEventListener('click', close); document.removeEventListener('keydown', close); };
  }, []);

  async function logout() {
    await api('/api/auth/logout', { method: 'POST', body: {} });
    qc.clear();
    navigate('/login');
    await qc.invalidateQueries({ queryKey: ['session'] });
  }

  // Project managers and engineers without other access see only the Projects area.
  const projectsOnly = isProjectsOnly(permissions);
  // The accounts department without other access: the Accounts tab (as home) and projects only.
  const accountsOnly = isAccountsOnly(permissions);
  const limited = projectsOnly || accountsOnly;
  const accountsTab = permissions.system.accountsWork || permissions.system.manageSetup || permissions.system.manageProjects ? [{ to: '/accounts', label: 'Accounts', end: false }] : [];
  // Logistics: the logistics team, administrators, project managers and engineers (read-only for them).
  const logisticsOnly = isLogisticsOnly(permissions);
  const logisticsTab = !logisticsOnly && (permissions.system.logisticsWork || permissions.system.manageSetup || permissions.system.manageProjects || permissions.system.workProjects)
    ? [{ to: '/logistics', label: 'Logistics', end: false }] : [];
  // Site check-in (scan the workers' QR, signatures): project managers, site engineers and foremen.
  const checkinTab = permissions.system.manageProjects || permissions.system.workProjects || permissions.system.manageSetup ? [{ to: '/projects/checkin', label: 'Site check-in', end: false }] : [];
  // Full CRM users switch between areas ("⋮⋮ Sales ▾"); the tab bar shows only the chosen area's tabs.
  const home: Tab = { to: '/', label: 'Home', end: true };
  const tab = (to: string) => TABS.find((t) => t.to === to)!;
  /** Pre-sales people (and not administrators): no sales Intelligence or Forecasts. */
  const presalesOnly = !!permissions.system.presalesWork && !permissions.system.manageSetup;
  // Projects: project people, and sales (their won deals' projects); not pre-sales, who don't run projects.
  const canProjectsArea = !!(permissions.system.manageProjects || permissions.system.workProjects || (permissions.objects.opportunity.read && !presalesOnly));
  const areas: Area[] = [
    { key: 'sales', label: 'Sales', icon: '', hint: 'Companies, contacts, opportunities, calendar', home: '/',
      tabs: [home, tab('/o/account'), tab('/o/contact'), tab('/o/opportunity'), tab('/calendar'), ...customObjects.map((o) => ({ to: `/c/${o.def.apiName}`, label: o.def.pluralLabel, end: false })),
        // The sales KPIs, for those allowed to see KPIs (the same page as Management → KPIs).
        ...(permissions.system.viewKpis ? [{ to: '/kpi', label: 'KPIs', end: false }] : [])] },
    // The other areas open on their own page (no sales Home tab in them).
    ...(permissions.system.presalesWork || permissions.system.manageSetup ? [{ key: 'presales', label: 'Pre-sales', icon: '', hint: 'My Desk: to acknowledge, in progress, clarifications, due dates', home: '/presales',
      tabs: [{ to: '/presales', label: 'My Desk', end: false }, tab('/o/opportunity'), tab('/calendar'), ...(permissions.system.viewKpis ? [{ to: '/kpi?view=presales', label: 'KPIs', end: false }] : [])] }] : []),
    // Pre-sales (not administrators) don't get the sales Intelligence, Forecasts and Reports pages.
    { key: 'management', label: 'Management', icon: '', hint: presalesOnly ? `${permissions.system.viewKpis ? 'KPIs, ' : ''}dashboards` : `Intelligence, forecasts, ${permissions.system.viewKpis ? 'KPIs, ' : ''}dashboards, reports`,
      home: ceo ? '/ceo' : presalesOnly ? (permissions.system.viewKpis ? '/kpi' : '/dashboards') : '/intelligence',
      tabs: [...(ceo ? [{ to: '/ceo', label: 'CEO', end: false }] : []), ...(presalesOnly ? [] : [tab('/intelligence'), tab('/forecasts')]), ...(permissions.system.viewKpis ? [{ to: '/kpi', label: 'KPIs', end: false }] : []), tab('/dashboards'), ...(presalesOnly ? [] : [tab('/reports')])] },
    ...(canProjectsArea ? [{ key: 'projects', label: 'Projects', icon: '', hint: 'Projects, site check-in, tools and manpower', home: '/projects', tabs: [{ to: '/projects', label: 'Projects', end: false }, ...checkinTab, ...logisticsTab] }] : []),
    ...(logisticsTab.length ? [{ key: 'logistics', label: 'Logistics', icon: '', hint: 'Shipments, material readiness, missing data', home: '/logistics', tabs: [...logisticsTab, ...(canProjectsArea ? [{ to: '/projects', label: 'Projects', end: false }] : [])] }] : []),
    ...(purchasing ? [{ key: 'purchasing', label: 'Purchasing', icon: '', hint: 'Requests from projects, supplier prices, Odoo POs to Logistics', home: '/purchasing', tabs: [{ to: '/purchasing', label: 'Purchasing', end: false }] }] : []),
    ...(accountsTab.length ? [{ key: 'accounts', label: 'Accounts', icon: '', hint: 'Customers to open in Odoo, approved purchases, expense claims', home: '/accounts', tabs: [...accountsTab, { to: '/expenses', label: 'Expenses', end: false }] }] : []),
    ...(permissions.system.manageUsers || permissions.system.manageSetup ? [{ key: 'setup', label: 'Setup', icon: '', hint: 'Users, access, stages, automation', home: '/setup', tabs: [{ to: '/setup', label: 'Setup', end: false }] }] : []),
  ];
  const switcher = !limited && !logisticsOnly;
  const areaNow = areaOf(areas, location.pathname, areaKey, location.search);
  const currentArea = areas.find((a) => a.key === areaNow) ?? areas[0]!;
  const tabs: Tab[] = accountsOnly
    ? [{ to: '/', label: 'Accounts', end: true }, { to: '/projects', label: 'Projects', end: false }, { to: '/expenses', label: 'Expenses', end: false }]
    : logisticsOnly
    ? [{ to: '/', label: 'Logistics', end: true }, { to: '/projects', label: 'Projects', end: false }]
    : projectsOnly
    ? [{ to: '/', label: 'Home', end: true }, { to: '/projects', label: 'Projects', end: false }, ...checkinTab, ...logisticsTab, ...accountsTab]
    : currentArea.tabs;
  // The menu on the left: every area the person has, with its pages (a page in two areas is listed once, in the first).
  // Pre-sales engineers see Pre-sales first.
  const ordered = firstArea === 'presales' ? [...areas.filter((x) => x.key === 'presales'), ...areas.filter((x) => x.key !== 'presales')] : areas;
  const seen = new Set<string>();
  const groups: Group[] = (switcher ? ordered.map((x) => ({ key: x.key, label: x.label, items: x.tabs }))
    : [{ key: accountsOnly ? 'accounts' : logisticsOnly ? 'logistics' : 'projects', label: accountsOnly ? 'Accounts' : logisticsOnly ? 'Logistics' : 'Projects', items: tabs }])
    .map((g) => ({ ...g, items: g.items.filter((t) => !seen.has(t.to) && seen.add(t.to)) }))
    .filter((g) => g.items.length);
  // The link marked as the page shown: the closest match (?view=… counts), in the current area first.
  let active: string | null = null;
  let best = 0;
  for (const g of groups) for (const t of g.items) {
    const sc = linkScore(t, location.pathname, location.search) * 2 + (g.key === areaNow ? 1 : 0);
    if (linkScore(t, location.pathname, location.search) && sc > best) { best = sc; active = `${g.key}|${t.to}`; }
  }
  const [mini, setMini] = useState(() => { try { return localStorage.getItem(MINI_KEY) === '1'; } catch { return false; } });
  const toggleMini = () => setMini((m) => { try { localStorage.setItem(MINI_KEY, m ? '0' : '1'); } catch { /* this visit only */ } return !m; });
  // Computers: ☰ hides the menu completely (the page takes the full width) and brings it back; remembered.
  const [hidden, setHidden] = useState(() => { try { return localStorage.getItem(HIDDEN_KEY) === '1'; } catch { return false; } });
  const toggleHidden = () => setHidden((h) => { try { localStorage.setItem(HIDDEN_KEY, h ? '0' : '1'); } catch { /* this visit only */ } return !h; });
  // Phones: the menu opens over the page from ☰ and closes when a page is chosen.
  const [drawer, setDrawer] = useState(false);
  useEffect(() => setDrawer(false), [location.pathname, location.search]);
  // A page opened: its cards rise in one after another and list rows slide in (what shows in the first moments).
  useEffect(() => {
    const main = () => document.querySelector<HTMLElement>('.content');
    const ts = [0, 250, 600].map((ms) => window.setTimeout(() => requestAnimationFrame(() => staggerPage(main())), ms));
    return () => ts.forEach((t) => window.clearTimeout(t));
  }, [location.pathname]);
  // A link with #place (e.g. a notification about a deal's submittals): scroll there once it shows, and highlight it.
  useEffect(() => {
    const id = location.hash.slice(1);
    if (!id) return;
    let tries = 0;
    const t = window.setInterval(() => {
      const el = document.getElementById(id);
      if (el || ++tries > 30) { window.clearInterval(t); if (el) jumpTo(el.closest('section') ?? el); }
    }, 150);
    return () => window.clearInterval(t);
  }, [location.pathname, location.hash]);
  // Opening a page of another area (a link, a popup) switches to that area and remembers it.
  useEffect(() => { if (switcher && areaNow !== areaKey) keepArea(areaNow); }, [switcher, areaNow, areaKey]);

  const canProjects = !!(permissions.system.manageProjects || permissions.system.workProjects || permissions.objects.opportunity.read);
  const bottom: BottomItem[] = accountsOnly
    ? [{ to: '/', label: 'Accounts', icon: 'deals', end: true }, { to: '/projects', label: 'Projects', icon: 'projects' }, { to: '/settings', label: 'Settings', icon: 'settings' }]
    : projectsOnly
    ? [{ to: '/', label: 'Home', icon: 'home', end: true }, { to: '/projects', label: 'Projects', icon: 'projects' },
      // Foremen and site engineers: the site check-in, one tap away on the phone.
      ...(checkinTab.length ? [{ to: '/projects/checkin', label: 'Check-in', icon: 'calendar' } as BottomItem] : []),
      ...(permissions.system.manageProjects ? [{ to: '/projects/manpower', label: 'Manpower', icon: 'people' } as BottomItem] : []),
      { to: '/settings', label: 'Settings', icon: 'settings' }]
    : [{ to: '/', label: 'Home', icon: 'home', end: true },
      ...(permissions.objects.opportunity.read ? [{ to: '/o/opportunity', label: 'Deals', icon: 'deals' } as BottomItem] : []),
      { to: '/calendar', label: 'Calendar', icon: 'calendar' },
      ...(canProjects ? [{ to: '/projects', label: 'Projects', icon: 'projects' } as BottomItem] : [])];

  const brand = (
    <Link to="/" className="brand" title={season && seasonOn ? SEASON_LABEL[season] : undefined}>
      {organization.logoVersion ? (
        <img className="brand-logo" src={`/api/org/logo?v=${organization.logoVersion}`} alt={organization.name} />
      ) : (
        <span className="brand-mark" aria-hidden="true">
          <svg viewBox="0 0 24 24"><path d="M4 16.5c2.5-1 4.2-3.4 5-6.5.8 3.1 2.6 5.5 5 6.5M12.5 8.5c1.6 2.3 4.2 3.6 7.5 3.5" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" /></svg>
        </span>
      )}
      {/* The logo carries the company name; without one, the name is shown. */}
      {!organization.logoVersion && <span className="brand-name">{organization.name}</span>}
    </Link>
  );

  return (
    <div className={`shell with-side${mini ? ' side-mini' : ''}${hidden ? ' side-hidden' : ''}${drawer ? ' side-open' : ''}`}>
      <LoginAsBanner />
      {/* Menu hidden: pointing at the left edge of the screen slides it out over the page. */}
      <div className="side-edge" aria-hidden="true" />
      <Sidebar groups={groups} active={active} mini={mini && !hidden} onMini={toggleMini} brand={brand} onSignOut={() => void logout()}
        onPick={(g) => { if (switcher) keepArea(g.key); }} />
      <button type="button" className="side-scrim" aria-label="Close the menu" tabIndex={-1} onClick={() => setDrawer(false)} />
      <div className="main-col">
      <header className={`topbar${season && seasonOn ? ` season-${season}` : ''}`}>
        <div className="topbar-row">
          <span className="topbar-start">
            <button type="button" className="icon-btn side-burger" aria-label={hidden ? 'Show the menu' : 'Hide the menu'} title={hidden ? 'Show the menu' : 'Hide the menu'}
              aria-expanded={drawer} onClick={() => { if (window.matchMedia?.('(max-width: 900px)').matches) setDrawer((d) => !d); else toggleHidden(); }}>
              <svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true"><path d="M3.5 5.5h13M3.5 10h13M3.5 14.5h13" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
            </button>
            {brand}
          </span>
          {limited ? <span className="topbar-spacer" /> : <GlobalSearch />}
          <div className="top-actions">
          {!limited && <details className="usermenu helpmenu">
            <summary className="icon-btn" aria-label="Help">
              <svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true"><circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" strokeWidth="1.6" /><path d="M7.8 7.6a2.3 2.3 0 1 1 3.2 2.1c-.7.3-1 .8-1 1.5v.4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /><circle cx="10" cy="14.4" r="1" fill="currentColor" /></svg>
            </summary>
            <div className="menu">
              <p><b>Help</b></p>
              <Link className="menu-link" to="/help?tour=play"><b>▶ Video tour (watch or try it)</b></Link>
              <Link className="menu-link" to="/help">User guide</Link>
              <Link className="menu-link" to="/import">Import data from a CSV file</Link>
              <Link className="menu-link" to="/reports">Build a report</Link>
              <Link className="menu-link" to="/settings">Set up two-step sign-in</Link>
              <Link className="menu-link" to="/recycle-bin">Restore something you deleted</Link>
              <p className="muted small">Questions? Contact your CRM administrator.</p>
            </div>
          </details>}
          {(permissions.system.manageUsers || permissions.system.manageSetup) && (
            <button className="icon-btn" aria-label="Quick settings" title="Settings" aria-haspopup="dialog" onClick={() => setQuick(true)}>
              <svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true"><path d="M10 6.8a3.2 3.2 0 1 0 0 6.4 3.2 3.2 0 0 0 0-6.4Zm6.6 4.2-1.4-.5a5.6 5.6 0 0 0 0-1l1.4-.5-1-2.6-1.4.5a5.5 5.5 0 0 0-.7-.7l.5-1.4-2.6-1-.5 1.4a5.6 5.6 0 0 0-1 0l-.5-1.4-2.6 1 .5 1.4a5.5 5.5 0 0 0-.7.7l-1.4-.5-1 2.6 1.4.5a5.6 5.6 0 0 0 0 1l-1.4.5 1 2.6 1.4-.5c.2.3.4.5.7.7l-.5 1.4 2.6 1 .5-1.4a5.6 5.6 0 0 0 1 0l.5 1.4 2.6-1-.5-1.4c.3-.2.5-.4.7-.7l1.4.5 1-2.6Z" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" /></svg>
            </button>
          )}
          <SuggestButton />
          <RefreshButton />
          <NotificationsBell />
          <details className="usermenu">
            <summary aria-label="Account menu"><span className="avatar">{user.name.slice(0, 1).toUpperCase()}</span></summary>
            <div className="menu">
              <p><b>{user.name}</b><br /><span className="muted">{user.email}</span></p>
              <Link className="menu-link" to="/settings">My settings</Link>
              <Link className="menu-link" to="/expenses">My expenses</Link>
              <Link className="menu-link" to="/compliance">My CRM compliance</Link>
              <Link className="menu-link" to="/help">Help &amp; guide</Link>
              <InstallMenuItem />
              {!limited && <Link className="menu-link" to="/import">Import data</Link>}
              {!limited && <Link className="menu-link" to="/recycle-bin">Recycle bin</Link>}
              {(permissions.system.manageUsers || permissions.system.manageSetup) && <Link className="menu-link" to="/setup">Setup</Link>}
              {season && <button type="button" className="menu-link link-btn" onClick={() => setSeasonOn(!seasonOn)}>{seasonOn ? `Turn off the ${SEASON_LABEL[season]} touch` : `Show the ${SEASON_LABEL[season]} touch`}</button>}
              <button className="btn ghost" onClick={logout}>Sign out</button>
            </div>
          </details>
          </div>
        </div>
      </header>
      <PullToRefresh />
      <main className="content"><InstallBanner /><BackBar />{children}</main>
      </div>
      <BottomNav items={bottom} />
      <Popups />
      <WelcomeIntro />
      <BackToTop />
      <ConnectionBar />
      <SessionTimer />
      {quick && <QuickSettings onClose={() => setQuick(false)} />}
    </div>
  );
}

/**
 * Back goes to the page the person came from whenever the browser has it: moving inside the CRM, and also after a
 * reload (⟳ or Ctrl + F5), which forgets the CRM's own record of the way here but not the browser's (the page came
 * from a CRM page then). Not to the sign-in page. Otherwise (a link opened in a new tab or from an email): up one level.
 */
export function goesBack(h: { key: string; length: number; referrer: string; origin: string }): boolean {
  if (h.length <= 1) return false;
  if (h.key !== 'default') return true;
  try {
    const from = new URL(h.referrer);
    return from.origin === h.origin && !/^\/(login|signup|auth|reset)/.test(from.pathname);
  } catch { return false; }
}

/**
 * "← Back" on every page below a tab (a deal, a company, a project, a report…): the installed phone app has
 * no browser back button. Goes back to where the user came from, or up one level when opened from a link.
 */
function BackBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const segs = location.pathname.split('/').filter(Boolean);
  const list = segs[0] === 'o' || segs[0] === 'c';
  // Printable project pages (report, period report, timeline) have their own "Back to the project" link.
  const ownBack = segs[0] === 'projects' && ['report', 'period', 'timeline'].includes(segs[2] ?? '');
  const show = !ownBack && (list ? segs.length >= 3 : segs.length >= 2 && segs[0] !== 'setup');
  if (!show) return null;
  const back = () => {
    if (goesBack({ key: location.key, length: window.history.length, referrer: document.referrer, origin: window.location.origin })) window.history.back();
    else navigate(`/${segs.slice(0, list ? 2 : 1).join('/')}`);
  };
  return (
    <div className="back-bar no-print">
      <button type="button" className="back-btn" onClick={back} aria-label="Back to the previous page">
        <span className="back-ico" aria-hidden="true">
          <svg viewBox="0 0 20 20" width="14" height="14"><path d="M12 4.5 6.5 10l5.5 5.5" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </span>
        Back
      </button>
    </div>
  );
}